// native
import { Component, OnInit, Input, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { Observable, Subject, merge, ReplaySubject } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

// service
import { ListService } from '../../../core/services/list.service';
import { SmartlistService } from '../../../core/services/smartlist.service';
import { ItemsDisplayService } from '../../../core/services/items-display.service';
import { CollectionsService } from '../../../core/services/collections.service';
import { NavigationService } from '../../../core/services/navigation.service';

// models
import { ISmartList, ICollection, ListNode, ListCreationPayload } from '../../../models';

@Component({
  selector: 'navigation-collection',
  templateUrl: './navigation-collection.component.html',
  styleUrls: ['./navigation-collection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationCollectionComponent implements OnInit {
  @Input()
  indent = 0;

  @Input()
  collection: ICollection;

  @Input()
  listExplorerMode?: boolean = false;

  @Output()
  onCreateList: EventEmitter<ListCreationPayload> = new EventEmitter<ListCreationPayload>();

  loadingSource = new Subject<boolean>();

  busy$: Observable<boolean>;

  loading$: Observable<boolean>;
  loadingLists$ = new ReplaySubject<boolean>(1);
  lists$: Observable<ListNode[]>;
  smartLists$: Observable<ISmartList[]>;

  expanded = true;
  maxListsDepth: number;
  editRoles: string[] = ['owner', 'admin', 'member'];
  manageRoles: string[] = ['owner', 'admin'];

  constructor(
    private listService: ListService,
    private smartlistService: SmartlistService,
    private itemsDisplayService: ItemsDisplayService,
    private collectionsService: CollectionsService,
    private navigationService: NavigationService
  ) {
    this.loading$ = this.loadingSource.asObservable();
    this.maxListsDepth = 7;
  }

  ngOnInit() {
    this.expanded = this.navigationService.isPrivateCollectionExpanded;

    this.loadingLists$.next(true);

    this.lists$ = this.listService.getListsTreeByCollectionId$(this.collection.id).pipe(
      tap(() => this.loadingLists$.next(false)),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.smartLists$ = this.smartlistService.getSmartlists({ collection_id: this.collection.id }).pipe(
      map(lists => lists.filter(list => !list.incomplete)),
      map(lists => lists.sort((a, b) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      })),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.busy$ = merge(this.loading$)
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

  }

  openCollectionItems() {
    if (this.listExplorerMode) {
      this.onCreateList.emit({ collectionId: this.collection.id });
      return;
    }

    this.itemsDisplayService.itemParams$.next({
      collection_id: this.collection.id
    });
    this.collectionsService.currentLibrary$.next(this.collection);
  }

  openRecentItems() {
    this.itemsDisplayService.itemParams$.next({
      collection_id: this.collection.id,
      recently_read: true
    });
  }

  openFlaggedItems() {
    this.itemsDisplayService.itemParams$.next({
      collection_id: this.collection.id,
      star: true
    });
  }

  onExpandedChange(isExpanded: boolean) {
    if (isExpanded && !this.listExplorerMode)
      this.loadingLists$.next(true);
    this.navigationService.setExpandedPrivateCollection(isExpanded);
  }
}
